import { collection, CollectionReference, getFirestore, onSnapshot, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';

import { getFirebaseTagsForOrganization } from '@/utils/getFirebaseTagsForOrganization';

import type { ITag, TagInDatabase } from '@/validations/tag';

const normalizeLabel = (value?: string): string =>
  (value ?? '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width chars
    .trim();

export function useFirebaseTags(organizationId: string, userId?: string) {
  const [tags, setTags] = useState<ITag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const getTags = useCallback(async () => {
    if (!organizationId || organizationId === '') {
      return;
    }

    const tags = await getFirebaseTagsForOrganization(organizationId, userId);

    if (tags) {
      setTags(tags);
    }
  }, [organizationId, userId]);

  useEffect(() => {
    if (!organizationId || organizationId === '') {
      setTags([]);
      return;
    }

    const firestore = getFirestore();

    // Tags collection reference
    const tagsCollection = collection(
      firestore,
      'organizations',
      organizationId,
      'tags',
    ) as CollectionReference<TagInDatabase>;
    const q = userId ? query(tagsCollection, where('user', '==', userId)) : query(tagsCollection);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const nextTags = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            const label = normalizeLabel(data.label);

            if (!label) return null;

            return {
              id: doc.id,
              label,
              user: (data.user ?? null) as string | null,
            };
          })
          .filter((t): t is { id: string; label: string; user: string | null } => t !== null);

        setTags(nextTags);
        setIsLoading(false);
      },
      (err) => {
        console.error('useFirebaseTags snapshot error:', err);
        setError(err);
        setIsLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [organizationId, userId]);

  return {
    tags,
    refetch: getTags,
  };
}
